'use client';

import { FC, useState } from 'react';
import {
  Check, X, ArrowRight, Play, ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Announcement } from '@/services/announcements';
import { getAnnouncementTypeConfig } from './typeConfig';
import AnnouncementVideo from './AnnouncementVideo';

interface AnnouncementStripViewProps {
  announcement: Announcement;
  onDismiss?: () => void;
  onCta?: () => void;
  className?: string;
}

/**
 * Full-width announcement strip. Presentational — used for the live vendor render and the
 * admin console preview. Inline-mode video expands in place; lightbox/link defer to onCta.
 */
const AnnouncementStripView: FC<AnnouncementStripViewProps> = ({ announcement, onDismiss, onCta, className }) => {
  const c = getAnnouncementTypeConfig(announcement.type);
  const spotlight = announcement.style === 'spotlight';
  const Icon = c.icon;
  const bullets = (announcement.bullets ?? []).filter((b) => b && b.trim());
  const isVideo = announcement.media_type === 'video';
  const inlineVideo = isVideo && announcement.video_mode === 'inline' && !!announcement.video_url;
  const [playing, setPlaying] = useState(false);
  const handleCta = () => {
    if (inlineVideo) setPlaying((p) => !p);
    else onCta?.();
  };
  const ctaLabel = inlineVideo && playing ? 'Hide video' : announcement.cta_label;

  return (
    <div
      data-testid="announcement-strip"
      className={cn(
        // Full-bleed bar (positioned by AnnouncementBanners). Bottom border + soft shadow, no radius.
        'relative w-full animate-fade-in-up overflow-hidden border-b',
        'shadow-[0_10px_28px_-18px_rgba(2,6,23,0.35)]',
        // Mockup navy (--mb-blue-800 #04216B): darker than Tailwind blue-800 so the mint
        // eyebrow/accent clears AA contrast on the spotlight variant.
        spotlight ? 'border-[#06205f] bg-[#04216b]' : cn(c.tintBg, c.tintBorder),
        className,
      )}
    >
      {spotlight && (
        // Blurred mint radial bleeding from the right — the signature spotlight glow.
        <span className="pointer-events-none absolute -top-[120%] right-[10%] h-80 w-[420px] rounded-full bg-emerald-400 opacity-50 blur-[150px]" />
      )}

      {/* Dismiss — a quiet corner affordance, kept clear of the primary CTA. */}
      {announcement.dismissible && onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className={cn(
            'absolute right-2.5 top-2.5 z-10 flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
            spotlight ? 'text-white/55 hover:bg-white/10 hover:text-white' : 'text-slate-400 hover:bg-slate-200/70 hover:text-slate-600',
          )}
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      )}

      <div className={cn('relative flex items-start gap-3.5 px-5 py-5', announcement.dismissible && onDismiss && 'pr-12')}>
        {/* Icon chip */}
        <div
          className={cn(
            'mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border',
            spotlight ? 'border-white/15 bg-white/10 text-emerald-300' : cn('bg-white', c.tintBorder, c.accentText),
          )}
        >
          <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
        </div>

        {/* Copy */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-col gap-0.5">
            <span className={cn('text-[10px] font-bold uppercase tracking-[0.09em]', spotlight ? 'text-emerald-300' : c.accentText)}>
              {c.label}
            </span>
            <span className={cn('text-[15px] font-semibold leading-tight tracking-tight', spotlight ? 'text-white' : 'text-slate-900')}>
              {announcement.title}
            </span>
          </div>
          {announcement.body && (
            <span className={cn('text-[13px] leading-relaxed', spotlight ? 'text-white/75' : 'text-slate-600')}>
              {announcement.body}
            </span>
          )}
          {bullets.length > 0 && (
            <ul className="mt-0.5 flex flex-row flex-wrap gap-x-5 gap-y-1 p-0">
              {bullets.map((it, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <Check className={cn('h-3.5 w-3.5 shrink-0', spotlight ? 'text-emerald-300' : c.accentText)} strokeWidth={2.6} />
                  <span className={cn('text-[12.5px] leading-snug', spotlight ? 'text-white/85' : 'text-slate-600')}>{it}</span>
                </li>
              ))}
            </ul>
          )}
          {inlineVideo && playing && (
            <div className="mt-1.5 max-w-[560px]">
              <AnnouncementVideo videoUrl={announcement.video_url as string} posterUrl={announcement.poster_url} />
            </div>
          )}
        </div>

        {/* Primary CTA — stands alone, vertically centered. */}
        {announcement.cta_label && (
          <button
            type="button"
            onClick={handleCta}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-[10px] px-3.5 py-2 text-[13px] font-semibold shadow-sm transition-all',
              spotlight ? 'bg-white text-blue-900 hover:bg-emerald-300' : cn('text-white', c.ctaBg),
            )}
          >
            {isVideo && !(inlineVideo && playing) && <Play className="h-3.5 w-3.5" strokeWidth={2.4} />}
            {ctaLabel}
            {inlineVideo && playing
              ? <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.4} />
              : !isVideo && <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />}
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementStripView;
